import { tokens } from '@/lib/tokens.js';
import { Component, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  hasError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error, hasError: true };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback !== undefined && this.state.error !== null) {
      return this.props.fallback(this.state.error);
    }

    return (
      <div
        role="alert"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: '2rem',
          color: tokens.colors.textDark,
          backgroundColor: tokens.colors.bgDark,
          fontFamily: tokens.font.sans,
        }}
      >
        <img
          src={tokens.logo.dark}
          alt="Quartup"
          style={{ height: '32px', marginBottom: '2rem' }}
        />
        <h1
          style={{
            color: tokens.colors.primary,
            fontSize: '1.5rem',
            fontWeight: 700,
            marginBottom: '0.5rem',
          }}
        >
          Se ha producido un error
        </h1>
        <p style={{ color: tokens.colors.textMutedDark, marginBottom: '1.5rem' }}>
          Por favor, recarga la página o contacta con soporte.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            backgroundColor: tokens.colors.primary,
            color: '#fff',
            border: 'none',
            borderRadius: tokens.radius.button,
            padding: '0.5rem 1.5rem',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Recargar
        </button>
      </div>
    );
  }
}
